import { getMediaUrl, isVttView } from "@/constants";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { getManifestData } from "@/helpers/getManifestData";
import { preloadAssets } from "@/helpers/preloadAssets";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useDiceSets } from "@/hooks/useDiceSets";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { Manifest } from "@/types";
import type { User } from "@dndbeyond/auth/types/user";
import { useEffect, useState } from "react";

export const useUserManifest = (
  nodeEnv?: string,
  env?: string,
  user?: User,
  preview?: boolean,
) => {
  const [userManifest, setUserManifest] = useState<Record<string, Manifest>>();
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const selectedSet = useDicePreviewerStore((state) => state.selectedSet);
  const additionalSetIds = useDicePreviewerStore(
    (state) => state.additionalSetIds,
  );
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const diceSets = useDiceSets(nodeEnv, env);
  const userSetId = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.setId,
  );

  useEffect(() => {
    if (isVttView || !(nodeEnv && env && diceSets)) return;

    const primarySetId = preview ? selectedSet?.id : userSetId;
    const activeSetIds: string[] = [];
    if (primarySetId) activeSetIds.push(String(primarySetId));
    if (preview) {
      for (const id of additionalSetIds) {
        if (id !== primarySetId && !activeSetIds.includes(id)) {
          activeSetIds.push(id);
        }
      }
    }

    if (activeSetIds.length === 0) return;

    (async () => {
      const next: Record<string, Manifest> = {};

      await Promise.all(
        activeSetIds.map(async (setId, index) => {
          const isPrimary = index === 0;
          const manifestUrl =
            (isPrimary && customUrlMap?.[customSetManifestFileName]) ||
            diceSets.find(({ id }) => id === setId)?.manifestUrl;

          if (!manifestUrl) {
            console.error(
              `[DiceCanvases] Manifest URL for set ID "${setId}" not found.`,
            );

            return;
          }

          const manifestData = await getManifestData({
            mediaUrl,
            setId,
            manifestUrl,
            ...(isPrimary && customUrlMap ? { customUrlMap } : {}),
          });

          if (!manifestData) return;

          next[setId] = manifestData;
          preloadAssets(
            mediaUrl,
            [manifestData],
            isPrimary ? customUrlMap : undefined,
          );
        }),
      );

      if (Object.keys(next).length === 0) return;

      setUserManifest(next);
    })();
  }, [
    additionalSetIds,
    customUrlMap,
    diceSets,
    env,
    mediaUrl,
    nodeEnv,
    preview,
    selectedSet?.id,
    userSetId,
  ]);

  return userManifest;
};
